export type ProductPersonalizationOptionType = "text" | "file"

export interface ProductPersonalizationOption {
  id: string
  type: ProductPersonalizationOptionType
  title: string
  description?: string
  maxChars?: number
}

export interface ProductPersonalizationConfig {
  enabled: boolean
  estimatedDeliveryBusinessDays: number
  options: ProductPersonalizationOption[]
}

export interface PersonalizationFileReference {
  key: string
  url: string
  originalName?: string
  contentType?: string
  extension?: string
  size?: number
  uploadedAt?: string
}

export interface CartItemPersonalizationSelection {
  optionId: string
  type: ProductPersonalizationOptionType
  title: string
  description?: string
  maxChars?: number
  textValue?: string
  uploadId?: string
  file?: PersonalizationFileReference
}

export interface PersonalizationUploadResponse {
  uploadId: string
  status: string
  optionId: string
  file: PersonalizationFileReference
}

export const PERSONALIZATION_TEXT_OPTION_MAX_CHARS = 2500
export const PERSONALIZATION_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024

export const PERSONALIZATION_ALLOWED_EXTENSIONS = [
  "jpg",
  "jpeg",
  "avif",
  "webp",
  "heic",
  "png",
  "mp4",
  "hevc",
  "mov",
  "pdf",
] as const

const PERSONALIZATION_MIME_BY_EXTENSION: Record<string, string[]> = {
  jpg: ["image/jpeg", "image/jpg"],
  jpeg: ["image/jpeg", "image/jpg"],
  png: ["image/png"],
  webp: ["image/webp"],
  avif: ["image/avif"],
  heic: ["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"],
  mp4: ["video/mp4", "application/mp4"],
  hevc: ["video/hevc", "video/h265", "video/x-h265", "video/mp4"],
  mov: ["video/quicktime"],
  pdf: ["application/pdf"],
}

export const PERSONALIZATION_FILE_ACCEPT_ATTRIBUTE =
  ".jpg,.jpeg,.avif,.webp,.heic,.png,.mp4,.hevc,.mov,.pdf"

const sanitizeText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : ""

const OBJECT_ID_REGEX = /^[a-fA-F0-9]{24}$/

const normalizeObjectIdLike = (value: unknown): string | undefined => {
  if (typeof value === "string" || typeof value === "number") {
    const candidate = String(value).trim()
    return OBJECT_ID_REGEX.test(candidate) ? candidate : undefined
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>

    if (record._id) {
      const nested = normalizeObjectIdLike(record._id)
      if (nested) return nested
    }

    if (typeof record.$oid === "string") {
      const fromOid = normalizeObjectIdLike(record.$oid)
      if (fromOid) return fromOid
    }

    if (typeof record.toString === "function") {
      const fromToString = normalizeObjectIdLike(record.toString())
      if (fromToString) return fromToString
    }
  }

  return undefined
}

const clampPositiveInt = (value: unknown, fallback = 0) => {
  const parsed = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback
  }
  return Math.floor(parsed)
}

const getFileExtension = (value: string): string => {
  const parts = value.toLowerCase().split(".")
  return parts.length > 1 ? parts[parts.length - 1] : ""
}

export const normalizeProductPersonalization = (
  input: unknown
): ProductPersonalizationConfig => {
  if (!input || typeof input !== "object") {
    return { enabled: false, estimatedDeliveryBusinessDays: 0, options: [] }
  }

  const raw = input as Record<string, unknown>
  const enabled = raw.enabled === true
  const estimatedDeliveryBusinessDays = clampPositiveInt(
    raw.estimatedDeliveryBusinessDays,
    0
  )

  const options = Array.isArray(raw.options)
    ? raw.options
        .map((entry, index): ProductPersonalizationOption | null => {
          if (!entry || typeof entry !== "object") return null
          const option = entry as Record<string, unknown>
          const type: ProductPersonalizationOptionType =
            option.type === "file" ? "file" : "text"
          const title = sanitizeText(option.title)
          if (!title) return null
          const id = sanitizeText(option.id) || `personalization-${index + 1}`
          const description = sanitizeText(option.description) || undefined

          if (type === "text") {
            const maxChars = Math.max(
              1,
              Math.min(
                PERSONALIZATION_TEXT_OPTION_MAX_CHARS,
                clampPositiveInt(option.maxChars, 250)
              )
            )
            return { id, type, title, description, maxChars }
          }

          return { id, type, title, description }
        })
        .filter((option): option is ProductPersonalizationOption => Boolean(option))
    : []

  if (!enabled) {
    return { enabled: false, estimatedDeliveryBusinessDays: 0, options: [] }
  }

  return {
    enabled: true,
    estimatedDeliveryBusinessDays,
    options,
  }
}

export const normalizeCartItemPersonalizationSelections = (
  input: unknown
): CartItemPersonalizationSelection[] => {
  if (!Array.isArray(input)) return []

  return input
    .map((entry): CartItemPersonalizationSelection | null => {
      if (!entry || typeof entry !== "object") return null
      const selection = entry as Record<string, unknown>
      const optionId = sanitizeText(selection.optionId)
      const title = sanitizeText(selection.title)
      if (!optionId || !title) return null
      const type: ProductPersonalizationOptionType =
        selection.type === "file" ? "file" : "text"
      const description = sanitizeText(selection.description) || undefined
      const maxChars = clampPositiveInt(selection.maxChars, 0) || undefined
      const textValue =
        typeof selection.textValue === "string" ? selection.textValue : undefined
      const uploadId = normalizeObjectIdLike(selection.uploadId)
      const fileRaw =
        selection.file && typeof selection.file === "object"
          ? (selection.file as Record<string, unknown>)
          : null
      const file =
        fileRaw && sanitizeText(fileRaw.key) && sanitizeText(fileRaw.url)
          ? {
              key: sanitizeText(fileRaw.key),
              url: sanitizeText(fileRaw.url),
              originalName: sanitizeText(fileRaw.originalName) || undefined,
              contentType: sanitizeText(fileRaw.contentType) || undefined,
              extension: sanitizeText(fileRaw.extension) || undefined,
              size: clampPositiveInt(fileRaw.size, 0) || undefined,
              uploadedAt: sanitizeText(fileRaw.uploadedAt) || undefined,
            }
          : undefined

      return {
        optionId,
        type,
        title,
        description,
        maxChars,
        textValue,
        uploadId,
        file,
      }
    })
    .filter((selection): selection is CartItemPersonalizationSelection =>
      Boolean(selection)
    )
}

export const addBusinessDays = (from: Date, businessDays: number): Date => {
  const safeFrom = new Date(from)
  if (Number.isNaN(safeFrom.getTime())) {
    return new Date()
  }

  const totalDays = Math.max(0, Math.floor(businessDays || 0))
  if (totalDays === 0) {
    return safeFrom
  }

  const result = new Date(safeFrom)
  let remaining = totalDays

  while (remaining > 0) {
    result.setDate(result.getDate() + 1)
    const day = result.getDay()
    if (day !== 0 && day !== 6) {
      remaining -= 1
    }
  }

  return result
}

export const formatEstimatedDeliveryDate = (date: Date): string => {
  return new Intl.DateTimeFormat("es-UY", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date)
}

export const validatePersonalizationFileForClient = (
  file: File
): { ok: true; extension: string } | { ok: false; error: string } => {
  const extension = getFileExtension(file.name || "")
  if (!extension || !PERSONALIZATION_ALLOWED_EXTENSIONS.includes(extension as (typeof PERSONALIZATION_ALLOWED_EXTENSIONS)[number])) {
    return {
      ok: false,
      error:
        "Formato no permitido. Tipos válidos: JPG, JPEG, AVIF, WEBP, HEIC, PNG, MP4, HEVC, MOV y PDF.",
    }
  }

  if (file.size > PERSONALIZATION_MAX_FILE_SIZE_BYTES) {
    return {
      ok: false,
      error: "El archivo debe pesar hasta 5MB.",
    }
  }

  const mime = (file.type || "").toLowerCase()
  const allowedMime = PERSONALIZATION_MIME_BY_EXTENSION[extension] || []
  if (mime && allowedMime.length > 0 && !allowedMime.includes(mime)) {
    return {
      ok: false,
      error: "El tipo de archivo seleccionado no es válido para la extensión indicada.",
    }
  }

  return { ok: true, extension }
}
